import React, { useCallback, useEffect, useState } from "react";
import { MailPlus, MailQuestion } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@core/hooks/use-toast";
import {
  listPollInvitations,
  sendInvitationReminders,
  isInReminderCooldown,
  type InvitationRow,
} from "@core/services/InvitationService";
import { AddInviteesDialog } from "@core/components/invitations/AddInviteesDialog";

interface PendingInvitationsBannerProps {
  pollId: string;
  /** Lien « Voir les invitations » — masqué si absent. */
  invitationsHref?: string;
  onNavigate?: (href: string) => void;
  /** Mode d'audience enregistré (« link », « emails », « group »). */
  audienceMode?: string;
}

/**
 * Invitations du sondage, sur la page Résultats du créateur.
 *
 * - Invités en attente : « 2 invités n'ont pas encore voté — Relancer les 2 ·
 *   Ajouter des invités · Voir les invitations ».
 * - Aucun invité en attente : un simple « Inviter par e-mail », pour ajouter
 *   des invités après publication (décision Julien, 2026-09-16).
 *
 * L'icône enveloppe de la barre d'outils ne disait ni ce qu'elle était ni
 * qu'il y avait quelque chose à faire : on pose l'information là où le
 * créateur regarde déjà — sous le titre — avec l'action à un clic.
 *
 * À ne monter que pour le créateur : les RPC refusent les autres.
 */
export const PendingInvitationsBanner: React.FC<
  PendingInvitationsBannerProps
> = ({ pollId, invitationsHref, onNavigate, audienceMode }) => {
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const [rows, setRows] = useState<InvitationRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listPollInvitations(pollId));
      setLoaded(true);
    } catch {
      // Non bloquant : sans liste lisible (ex. pas propriétaire), on
      // n'affiche rien plutôt qu'une action qui échouerait.
      setRows([]);
      setLoaded(false);
    }
  }, [pollId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = rows.filter(
    (r) => !r.has_voted && r.status !== "revoked" && r.status !== "voted",
  );
  const remindable = pending.filter((r) => !isInReminderCooldown(r));

  // Rien tant que la liste n'est pas lue : évite un « Inviter par e-mail »
  // qui clignote avant l'arrivée des invités réels.
  if (!loaded) return null;

  const handleRemind = async () => {
    setSending(true);
    try {
      const res = await sendInvitationReminders(
        pollId,
        remindable.map((r) => r.id),
      );
      if (res.successCount > 0) {
        toast({
          title: t("invitations.tracking.action_resent_bulk", {
            count: res.successCount,
          }),
        });
      } else {
        toast({
          title: t("invitations.error_title"),
          description: t("invitations.tracking.action_resent_bulk_error", {
            count: res.failedCount,
          }),
          variant: "destructive",
        });
      }
      await load();
    } finally {
      setSending(false);
    }
  };

  const dialog = (
    <AddInviteesDialog
      pollId={pollId}
      open={addOpen}
      onOpenChange={setAddOpen}
      audienceMode={audienceMode}
      onAdded={() => void load()}
    />
  );

  if (pending.length === 0) {
    return (
      <>
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="results-invite-by-email"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-2 hover:underline"
          >
            <MailPlus className="h-4 w-4" />
            {t("invitations.add.results_cta")}
          </button>
        </div>
        {dialog}
      </>
    );
  }

  return (
    <>
      <div
        className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-lg border border-border bg-muted px-4 py-2.5 text-sm"
        data-testid="results-pending-invitations"
      >
        <MailQuestion className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-foreground">
          {t("invitations.tracking.pending_banner", { count: pending.length })}
        </span>
        {remindable.length > 0 ? (
          <button
            type="button"
            onClick={handleRemind}
            disabled={sending}
            data-testid="results-pending-invitations-remind"
            className="font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
          >
            {t("invitations.tracking.pending_banner_remind", {
              count: remindable.length,
            })}
          </button>
        ) : (
          <span className="text-muted-foreground">
            {t("invitations.tracking.resend_all_cooldown")}
          </span>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid="results-pending-invitations-add"
          className="text-primary underline-offset-2 hover:underline"
        >
          {t("invitations.add.open_button")}
        </button>
        {invitationsHref && (
          <>
            <span aria-hidden className="text-muted-foreground">
              ·
            </span>
            <button
              type="button"
              onClick={() => onNavigate?.(invitationsHref)}
              className="text-muted-foreground underline-offset-2 hover:underline"
            >
              {t("invitations.tracking.pending_banner_see_all")}
            </button>
          </>
        )}
      </div>
      {dialog}
    </>
  );
};

export default PendingInvitationsBanner;
